import type { HTMLAttributes, ReactNode } from "react";
import { createStyles } from "./styling";
import { LiquidGlass } from "./LiquidGlass";

const useStyles = createStyles(({ css, token }) => ({
  actions: css`
    align-items: center;
    display: flex;
    flex: 0 0 auto;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-end;
  `,
  body: css`
    background: ${token.colorBgLayout};
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: 16px 24px 24px;

    @media (max-width: ${token.screenSMMax}px) {
      padding: 12px;
    }
  `,
  copy: css`
    min-width: 0;

    h2 {
      font-size: 20px;
      line-height: 1.35;
      margin: 0;
    }
  `,
  description: css`
    display: block;
    font-size: 13px;
    margin-top: 2px;
  `,
  header: css`
    align-items: center;
    background: ${token.colorBgContainer};
    border-bottom: 1px solid ${token.colorBorderSecondary};
    display: flex;
    flex: 0 0 auto;
    gap: 20px;
    justify-content: space-between;
    min-height: 62px;
    padding: 12px 22px;

    @media (max-width: ${token.screenSMMax}px) {
      align-items: flex-start;
      flex-direction: column;
      gap: 12px;
      padding: 14px 16px;

      .ark-workspace-header-actions {
        justify-content: flex-start;
        width: 100%;
      }
    }
  `,
  icon: css`
    align-items: center;
    background: ${token.colorPrimaryBg};
    border-radius: 9px;
    color: ${token.colorPrimary};
    display: inline-flex;
    flex: 0 0 36px;
    font-size: 18px;
    height: 36px;
    justify-content: center;
    width: 36px;
  `,
  identity: css`
    align-items: center;
    display: flex;
    gap: 12px;
    min-width: 0;
  `,
  root: css`
    background: ${token.colorBgLayout};
    display: flex;
    flex-direction: column;
    height: var(--ark-workspace-height, auto);
    min-height: 520px;
    overflow: hidden;
  `,
}));

export function WorkspacePage({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  const { cx, styles } = useStyles();
  return (
    <div className={cx(styles.root, "ark-effects-canvas", className)} {...props}>
      {children}
    </div>
  );
}

export function WorkspacePageHeader({
  actions,
  className,
  description,
  icon,
  title,
  ...props
}: Omit<HTMLAttributes<HTMLElement>, "title"> & {
  actions?: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  title: ReactNode;
}) {
  const { cx, styles } = useStyles();
  return (
    <LiquidGlass as="header" className={cx(styles.header, "ark-glass-bar", className)} {...props}>
      <div className={styles.identity}>
        {icon ? <span className={styles.icon}>{icon}</span> : null}
        <div className={styles.copy}>
          <h2>{title}</h2>
          {description ? (
            <span className={styles.description} style={{ color: "var(--ark-text-secondary)" }}>
              {description}
            </span>
          ) : null}
        </div>
      </div>
      {actions ? (
        <div className={cx(styles.actions, "ark-workspace-header-actions")}>
          {actions}
        </div>
      ) : null}
    </LiquidGlass>
  );
}

export function WorkspacePageBody({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  const { cx, styles } = useStyles();
  return (
    <div className={cx(styles.body, className)} {...props}>
      {children}
    </div>
  );
}
